import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Skeleton,
} from '@ouropix/ui';
import {
  Mail,
  MessageSquare,
  Bell,
  AlertTriangle,
  RefreshCw,
  RotateCcw,
  CheckCircle,
  Clock,
  XCircle,
  Inbox,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { api } from '@/lib/api';

export const Route = createFileRoute('/_admin/alertas/entregas')({
  component: EntregasPage,
});

// ─── Types ────────────────────────────────────────────────────────────────────

interface DeliveryFailure {
  id: string;
  user_id?: string;
  template?: string;
  channel?: string;
  error?: string;
  attempts?: number;
  failed_at?: string;
  status?: string;
}

interface DeliveryStats {
  since: string;
  since_hours: number;
  total: number;
  sent: number;
  failed: number;
  delivery_rate: number;
  by_status: Record<string, number>;
  by_channel: Record<string, number>;
  queue_depth: number;
  queue_metrics: Record<string, number>;
  recent_failures: DeliveryFailure[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(d?: string | null) {
  if (!d) return '—';
  try {
    return format(new Date(d), 'dd/MM/yy HH:mm', { locale: ptBR });
  } catch {
    return d;
  }
}

function maskUserId(id?: string) {
  if (!id) return '—';
  if (id.length <= 8) return id;
  return `${id.slice(0, 4)}…${id.slice(-4)}`;
}

function pct(value: number, total: number) {
  if (!total) return '0%';
  return `${Math.round((value / total) * 100)}%`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  valueClass,
}: {
  label: string;
  value: number | string;
  sub?: string;
  icon: React.ComponentType<{ className?: string }>;
  valueClass?: string;
}) {
  return (
    <Card className="bg-card/50">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${valueClass ?? ''}`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <Icon className="h-5 w-5 text-muted-foreground mt-0.5" />
        </div>
      </CardContent>
    </Card>
  );
}

function ChannelCard({
  channel,
  count,
  total,
}: {
  channel: string;
  count: number;
  total: number;
}) {
  const icons: Record<string, React.ComponentType<{ className?: string }>> = {
    email: Mail,
    sms: MessageSquare,
    push: Bell,
  };
  const Icon = icons[channel.toLowerCase()] ?? Bell;
  return (
    <div className="flex items-center gap-3 py-2">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between text-sm mb-1">
          <span className="capitalize">{channel}</span>
          <span className="text-muted-foreground">{count} ({pct(count, total)})</span>
        </div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-primary rounded-full"
            style={{ width: pct(count, total) }}
          />
        </div>
      </div>
    </div>
  );
}

function QueueGauge({ depth }: { depth: number }) {
  const color =
    depth === 0 ? 'text-green-400' : depth < 50 ? 'text-yellow-400' : 'text-red-400';
  return (
    <div className="flex items-center gap-3">
      <Inbox className="h-5 w-5 text-muted-foreground" />
      <div>
        <p className="text-xs text-muted-foreground">Fila BullMQ</p>
        <p className={`text-2xl font-bold ${color}`}>{depth}</p>
        <p className="text-xs text-muted-foreground">jobs pendentes</p>
      </div>
    </div>
  );
}

function ChannelBadge({ channel }: { channel?: string }) {
  const map: Record<string, { label: string; class: string }> = {
    email: { label: 'Email', class: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    sms: { label: 'SMS', class: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
    push: { label: 'Push', class: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
  };
  const cfg = map[(channel ?? '').toLowerCase()];
  if (!cfg) return <span className="text-xs text-muted-foreground">{channel ?? '—'}</span>;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${cfg.class}`}>
      {cfg.label}
    </span>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function EntregasPage() {
  const [sinceHours, setSinceHours] = useState(24);
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch, isFetching } = useQuery<DeliveryStats>({
    queryKey: ['delivery-stats', sinceHours],
    queryFn: () =>
      api.get('/admin/alerts/delivery-stats', { params: { since_hours: sinceHours } }).then((r) => r.data),
    refetchInterval: 30_000,
    retry: 2,
    retryDelay: 1000,
  });

  const retryMutation = useMutation({
    mutationFn: (deliveryId: string) =>
      api.post(`/admin/alerts/retry/${deliveryId}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-stats'] });
    },
  });

  const total = data?.total ?? 0;
  const sent = data?.sent ?? 0;
  const failed = data?.failed ?? 0;
  const queued = data?.by_status?.['queued'] ?? data?.by_status?.['pending'] ?? 0;
  const deliveryRate = data?.delivery_rate ?? 0;
  const byChannel = data?.by_channel ?? {};
  const queueDepth = data?.queue_depth ?? 0;
  const recentFailures = data?.recent_failures ?? [];

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Entregas de Notificacoes</h1>
          <p className="text-muted-foreground text-sm">
            Stats de entrega por canal, fila e falhas recentes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sinceHours}
            onChange={(e) => setSinceHours(Number(e.target.value))}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm"
            aria-label="Janela de tempo"
          >
            <option value={6}>6h</option>
            <option value={24}>24h</option>
            <option value={48}>48h</option>
            <option value={72}>72h</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Stats grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="bg-card/50">
              <CardContent className="p-4">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="bg-card/50">
          <CardContent className="flex flex-col items-center py-10 gap-3">
            <AlertTriangle className="h-10 w-10 text-yellow-500" />
            <p className="text-muted-foreground">Erro ao carregar stats de entrega.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              label="Enviadas"
              value={sent}
              sub={pct(sent, total) + ' do total'}
              icon={CheckCircle}
              valueClass="text-green-400"
            />
            <StatCard
              label="Na fila"
              value={queued}
              sub={pct(queued, total) + ' do total'}
              icon={Clock}
              valueClass="text-yellow-400"
            />
            <StatCard
              label="Falhas"
              value={failed}
              sub={pct(failed, total) + ' do total'}
              icon={XCircle}
              valueClass="text-red-400"
            />
            <StatCard
              label="Taxa de entrega"
              value={`${Math.round(deliveryRate * 100) / 100}%`}
              sub={`${total} entregas totais`}
              icon={RefreshCw}
            />
          </div>

          {/* Channel breakdown + Queue gauge */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-card/50">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium">Por Canal</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {Object.keys(byChannel).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem dados de canal disponivel.</p>
                ) : (
                  <div className="space-y-1">
                    {Object.entries(byChannel).map(([ch, count]) => (
                      <ChannelCard key={ch} channel={ch} count={count as number} total={total} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-card/50">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium">Fila de Processamento</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <QueueGauge depth={queueDepth} />
                {data?.queue_metrics && Object.keys(data.queue_metrics).length > 1 && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {Object.entries(data.queue_metrics)
                      .filter(([k]) => k !== 'queue_depth')
                      .slice(0, 4)
                      .map(([k, v]) => (
                        <div key={k} className="text-xs">
                          <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}:</span>{' '}
                          <span className="font-medium">{String(v)}</span>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent failures table */}
          <Card className="bg-card/50">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-medium">Falhas Recentes</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {recentFailures.length === 0 ? (
                <div className="flex flex-col items-center py-10 gap-2">
                  <CheckCircle className="h-10 w-10 text-green-500" />
                  <p className="text-sm text-muted-foreground">Nenhuma falha recente.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" aria-label="Falhas recentes de entrega">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Usuario</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground hidden sm:table-cell">Template</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Canal</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground hidden md:table-cell">Erro</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Tentativas</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground hidden lg:table-cell">Falhou em</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Acao</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentFailures.map((f) => (
                        <tr
                          key={f.id}
                          className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <code className="text-xs text-muted-foreground">{maskUserId(f.user_id)}</code>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <span className="text-xs">{f.template ?? '—'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <ChannelBadge channel={f.channel} />
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className="text-xs text-red-400 truncate max-w-[200px] block" title={f.error}>
                              {f.error ?? '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="text-xs">
                              {f.attempts ?? 1}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <span className="text-xs text-muted-foreground font-mono">{fmt(f.failed_at)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={retryMutation.isPending}
                              onClick={() => retryMutation.mutate(f.id)}
                              aria-label={`Retentar entrega ${f.id}`}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Retry
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
